import { computed, provide, ref, watch } from 'vue';
import { useSetUpPermissionsQuery } from '@/generated/graphql';
import { defineAbilitiesFor, type FeaturesAndPermissions } from './abilities';
import {
	FEATURES_PROVIDE_KEY,
	type PermissionsProvideValue,
	PERMISSIONS_PROVIDE_KEY,
	SITE_MODULES_PROVIDE_KEY,
} from './usePermissions';
import { FEATURE_FLAGS_PROVIDE_KEY } from '@/composables/permissions/useFeatureFlags';

/**
 * A composable for fetching, configuring, and providing permissions to all
 * child components.
 */
export const useSetUpPermissions = () => {
	const { result } = useSetUpPermissionsQuery({
		fetchPolicy: 'cache-first',
	});
	const ability: PermissionsProvideValue = ref(undefined);

	const features = computed(() =>
		(result.value?.EnabledFeatures ?? []).map((feature) => feature.name)
	);
	const siteModules = computed(() =>
		(result.value?.EnabledModules ?? []).map(({ name }) => name)
	);

	const featureFlags = computed(() => result.value?.FeatureFlags ?? []);

	watch(
		result,
		() => {
			if (
				!result.value?.EnabledFeatures ||
				!result.value?.FeatureFlags ||
				!result.value?.EnabledPermissions
			) {
				return;
			}

			ability.value = defineAbilitiesFor(
				[
					...result.value.EnabledFeatures.map(
						(feature) => feature.name
					),
					...result.value.EnabledPermissions.map(
						(permission) => permission.name
					),
				] as FeaturesAndPermissions[],
				result.value.CurrentUser
			);
		},
		{ immediate: true }
	);

	provide(PERMISSIONS_PROVIDE_KEY, ability);
	provide(FEATURES_PROVIDE_KEY, features);
	provide(SITE_MODULES_PROVIDE_KEY, siteModules);
	provide(FEATURE_FLAGS_PROVIDE_KEY, featureFlags);

	return { ability, features, featureFlags, siteModules };
};
